/**
 * @file projectDuration.ts
 * @brief Helpers to estimate project hours from a start/end date range.
 * @author Nestbotics
 * @date 2026-06-19
 */

import dayjs, { type Dayjs } from 'dayjs';

export const DEFAULT_WORK_HOURS_PER_DAY = 8;

export interface ProjectDurationEstimate {
  businessDays: number;
  workHoursPerDay: number;
  totalHours: number;
}

/**
 * @brief Counts inclusive weekdays (Mon–Fri) between two dates.
 * @param start Start date.
 * @param end End date.
 * @return Number of business days, or 0 when the range is invalid.
 */
export function countBusinessDays(start: Dayjs, end: Dayjs): number {
  if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) {
    return 0;
  }

  let count = 0;
  let current = start.startOf('day');
  const last = end.startOf('day');

  while (!current.isAfter(last)) {
    const weekday = current.day();
    if (weekday !== 0 && weekday !== 6) {
      count += 1;
    }
    current = current.add(1, 'day');
  }

  return count;
}

/**
 * @brief Estimates project hours from a date range using business days.
 * @param startDate Project start date.
 * @param endDate Project end date.
 * @param workHoursPerDay Hours per working day (default 8).
 * @return Estimate details, or null when dates are missing or invalid.
 */
export function getProjectDurationEstimate(
  startDate: dayjs.ConfigType | null | undefined,
  endDate: dayjs.ConfigType | null | undefined,
  workHoursPerDay = DEFAULT_WORK_HOURS_PER_DAY
): ProjectDurationEstimate | null {
  if (startDate == null || endDate == null) return null;

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const businessDays = countBusinessDays(start, end);

  if (businessDays <= 0) return null;

  const hoursPerDay = Math.max(1, workHoursPerDay);
  const totalHours = Math.round(businessDays * hoursPerDay * 100) / 100;

  return {
    businessDays,
    workHoursPerDay: hoursPerDay,
    totalHours,
  };
}

/**
 * @brief Returns estimated hours only, for form auto-fill.
 * @param startDate Project start date.
 * @param endDate Project end date.
 * @param workHoursPerDay Hours per working day.
 * @return Estimated hours or null.
 */
export function calculateEstimatedHoursFromDates(
  startDate: dayjs.ConfigType | null | undefined,
  endDate: dayjs.ConfigType | null | undefined,
  workHoursPerDay = DEFAULT_WORK_HOURS_PER_DAY
): number | null {
  return getProjectDurationEstimate(startDate, endDate, workHoursPerDay)?.totalHours ?? null;
}

/**
 * @brief Builds helper text describing how estimated hours were calculated.
 * @param estimate Duration estimate from getProjectDurationEstimate.
 * @return Human-readable helper string.
 */
export function formatProjectDurationHint(estimate: ProjectDurationEstimate): string {
  return `${estimate.businessDays} working day${estimate.businessDays === 1 ? '' : 's'} × ${estimate.workHoursPerDay} hrs/day`;
}

/** Converts stored minutes to hours for form display. */
export function minutesToHours(minutes?: number | null): number | null {
  if (minutes == null || Number.isNaN(minutes)) return null;
  return Math.round((minutes / 60) * 100) / 100;
}

/** Converts hours entered in a form to minutes for API storage. */
export function hoursToMinutes(hours?: number | null): number | undefined {
  if (hours == null || Number.isNaN(hours)) return undefined;
  return Math.round(hours * 60);
}
